<script lang="ts">
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import type { SbImage, SbLink } from '$lib/storyblok/types';
	import Button from '$lib/buttons/v3/button.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import Image from '$lib/storyblok/components/image.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import { scrollPosition } from '$lib/actions/scrollPosition';
	import { convertRange } from '$lib/utils/number';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';

	interface Props {
		content: ISbStoryData<{
			headline: string;
			subline: ISbRichtext;
			background_asset: SbImage;
			background_asset_desktop?: SbImage;
			foreground_asset?: SbImage;
			foreground_asset_desktop?: SbImage;
			foreground_offset?: string;
			cta_label: string;
			cta_link: SbLink;
			color: 'brown-1' | 'white';
			background_asset_alignment: 'top' | 'center' | 'bottom';
		}>['content'];
	}

	let { content }: Props = $props();

	let offsetPercentage = $state(0);

	let offset = $derived(content.foreground_offset === undefined ? 300 : +content.foreground_offset);
	let translateY = $derived(offset - convertRange(Math.abs(offsetPercentage), 0, 50, 0, offset));

	let textColor = $derived(`text-${content.color || 'brown-1'}`);
</script>

<section
	id="hero"
	class="relative overflow-hidden"
	use:editable={content}
	use:scrollPosition
	onchangescroll={({ detail }) => (offsetPercentage = detail.offsetPercentage)}
>
	{#if content.background_asset.filename}
		<Image
			loading="eager"
			src={content.background_asset.filename}
			wrapperClass="absolute z-0 inset-0 w-full h-full {content.background_asset_desktop?.filename
				? 'lg:hidden'
				: ''}"
			class="h-full w-full object-cover object-{content.background_asset_alignment || 'bottom'}"
			width={430}
			sizes={{
				430: { width: 860 },
				1024: { width: 2048 },
				1440: { width: 2880 },
				1920: { width: 3840 },
				2560: { width: 5120 }
			}}
		/>
	{/if}

	{#if content.background_asset_desktop?.filename}
		<Image
			loading="eager"
			src={content.background_asset_desktop.filename}
			wrapperClass="absolute z-0 inset-0 w-full h-full max-lg:hidden"
			class="h-full w-full object-cover object-{content.background_asset_alignment || 'bottom'}"
			width={430}
			sizes={{
				430: { width: 860 },
				1024: { width: 2048 },
				1440: { width: 2880 },
				1920: { width: 3840 },
				2560: { width: 5120 }
			}}
		/>
	{/if}

	{#if content.foreground_asset?.filename}
		<Image
			loading="eager"
			style="transform: translate3d(0, {translateY}px, 0)"
			src={content.foreground_asset.filename}
			wrapperClass="absolute inset-x-0 bottom-0 w-full {content.foreground_asset_desktop?.filename
				? 'lg:hidden'
				: ''}"
			class="h-full w-full object-cover object-top"
			width={430}
			sizes={{
				430: { width: 860 },
				1024: { width: 2048 },
				1440: { width: 2880 },
				1920: { width: 3840 },
				2560: { width: 5120 }
			}}
		/>
	{/if}

	{#if content.foreground_asset_desktop?.filename}
		<Image
			loading="eager"
			style="transform: translate3d(0, {translateY}px, 0)"
			src={content.foreground_asset_desktop.filename}
			wrapperClass="absolute inset-x-0 bottom-0 w-full max-lg:hidden"
			class="h-full w-full object-cover object-top"
			width={430}
			sizes={{
				430: { width: 860 },
				1024: { width: 2048 },
				1440: { width: 2880 },
				1920: { width: 3840 },
				2560: { width: 5120 }
			}}
		/>
	{/if}

	<PageGrid16 margin class="relative z-10" gridClass="text-center">
		<h1
			class="{textColor} col-span-full text-balance text-[52px]/[108%] tracking-[-1.04px] lg:text-[96px]/[100%] lg:tracking-[-1.92px]"
		>
			{content.headline}
		</h1>

		<div
			class="{textColor} col-span-full mt-4 text-[20px]/[140%] tracking-[-.2px] empty:hidden lg:col-start-3 lg:col-end-[15] lg:text-[24px]/[140%] lg:tracking-[-.24px]"
		>
			<SbRichtext text={content.subline} />
		</div>
		<div class="col-span-full mt-6 flex justify-center lg:mt-8">
			<Button
				href={getSbUrl(content.cta_link)}
				class="{content.color === 'brown-1' ? 'btn-brown' : 'btn-white'} btn-large"
				>{content.cta_label}</Button
			>
		</div>
	</PageGrid16>
</section>

<style lang="scss">
	section {
		height: calc(100svh - var(--banner-height));

		transition-property: padding-top, height;
		transition-duration: 0.2s;
		transition-timing-function: ease-out;

		padding-top: calc(var(--full-header-height) + 80px);

		@media (min-width: 1024px) {
			padding-top: calc(var(--full-header-height) + 120px);
		}
	}
</style>
